/**
 * Geocoding Service
 *
 * Handles address searching and geocoding.
 */

// Mapbox access token - from environment variables or fallback
const MAPBOX_TOKEN = process.env?.MAPBOX_TOKEN;

// Function to merge an array of objects into a single object
function mergeObjects(dataArray) {
  if (!dataArray || dataArray.length === 0) {
    return {};
  }

  // Only focus on merging the attributes
  const result = {
    attributes: {},
  };

  // Get all attribute keys from all objects
  const allAttributeKeys = new Set();
  dataArray.forEach((obj) => {
    Object.keys(obj.attributes).forEach((key) => {
      allAttributeKeys.add(key);
    });
  });

  // Merge the attributes
  allAttributeKeys.forEach((key) => {
    // Check if this is an array attribute in any of the objects
    const isArrayAttr = dataArray.some(
      (obj) =>
        obj.attributes[key] !== null && Array.isArray(obj.attributes[key])
    );

    if (isArrayAttr) {
      // Merge arrays and keep duplicates
      result.attributes[key] = [];
      dataArray.forEach((obj) => {
        if (
          obj.attributes[key] !== null &&
          Array.isArray(obj.attributes[key])
        ) {
          result.attributes[key] = [
            ...result.attributes[key],
            ...obj.attributes[key],
          ];
        }
      });
    } else {
      // For non-array attributes, take the first non-null value
      const nonNullValues = dataArray
        .map((obj) => obj.attributes[key])
        .filter((val) => val !== null && val !== undefined);

      result.attributes[key] =
        nonNullValues.length > 0 ? nonNullValues[0] : null;
    }
  });

  return result;
}

// Usage example:
// const data = JSON.parse(jsonString);
// const mergedAttributes = mergeObjects(data).attributes;
// console.log(mergedAttributes);

/**
 * Get the Mapbox token (async function)
 * @returns {Promise<string>} The Mapbox token
 */
export async function getMapboxToken() {
  return await MAPBOX_TOKEN;
}

/**
 * Search addresses using the geocoding API
 * @param {string} query The search query
 * @returns {Promise} Promise resolving to search results
 */
export function searchAddresses(query) {
  if (!query || query.trim() === "") {
    return Promise.resolve({ results: [] });
  }

  return fetch(
    `https://api3.geo.admin.ch/rest/services/api/SearchServer?features=ch.bfs.gebaeude_wohnungs_register&type=featuresearch&searchText=${encodeURIComponent(
      query
    )}`
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Geocoding error: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      // Process results if needed
      return data;
    })
    .catch((error) => {
      throw error;
    });
}

/**
 * Geocode an address to get coordinates
 * @param {string} address The address to geocode
 * @returns {Promise} Promise resolving to coordinates
 */
export function geocodeAddress(address) {
  if (!address) {
    return Promise.reject(new Error("No address provided"));
  }

  return getMapboxToken()
    .then((token) => {
      return fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          address
        )}.json?access_token=${token}&country=CH`
      );
    })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Geocoding error: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        return {
          longitude: feature.center[0],
          latitude: feature.center[1],
          place_name: feature.place_name,
        };
      }
      throw new Error("No coordinates found");
    })
    .catch((error) => {
      throw error;
    });
}

/**
 * Reverse geocode coordinates to get address
 * @param {number} longitude The longitude
 * @param {number} latitude The latitude
 * @returns {Promise} Promise resolving to address
 */
export function reverseGeocode(longitude, latitude) {
  if (!longitude || !latitude) {
    return Promise.reject(new Error("No coordinates provided"));
  }

  return getMapboxToken()
    .then((token) => {
      return fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${token}&country=CH`
      );
    })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Reverse geocoding error: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        return {
          place_name: feature.place_name,
          address: extractAddressComponents(feature),
        };
      }
      throw new Error("No address found");
    })
    .catch((error) => {
      throw error;
    });
}

/**
 * Extract address components from a Mapbox feature
 * @param {Object} feature The Mapbox feature
 * @returns {Object} Address components
 */
function extractAddressComponents(feature) {
  const context = feature.context || [];
  let street, city, zip, country;

  // Extract street from place name
  street = feature.text || "";

  // Extract other components from context
  context.forEach((item) => {
    const id = item.id.split(".")[0];
    const text = item.text;

    if (id === "postcode") {
      zip = text;
    } else if (id === "place") {
      city = text;
    } else if (id === "country") {
      country = text;
    }
  });

  return {
    street,
    city,
    zip,
    country: country || "Switzerland",
  };
}

// Function to make API call to get building details
export function fetchBuildingDetails(selectedProperty) {
  if (!selectedProperty || !selectedProperty.featureId) {
    return Promise.resolve(null);
  }

  // Extract the feature ID without the underscore
  const featureId = selectedProperty.featureId.split("_")[0];

  return fetch(
    `https://api3.geo.admin.ch/rest/services/api/MapServer/find?layer=ch.bfs.gebaeude_wohnungs_register&searchText=${featureId}&searchField=egid&returnGeometry=false&contains=false`
  )
    .then((response) => response.json())
    .then((data) => {
      if (data.results && data.results.length > 0) {
        // return building details


        return mergeObjects(data.results).attributes;
      }
      return null;
    })
    .catch((error) => {
      return null;
    });
}
